import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import marketplaceService, {
    extractErrorMessage,
    SellerDashboard,
    SellerPeriod,
    SellerSale,
} from '../../../services/marketplaceService';
import { formatEuro } from '../../../pages/marketplaceVisibility';
import { tokens } from '../../../designTokens';
import MaterialSymbol from '../../common/MaterialSymbol';
import { IconBadge, VisibilityBadge } from './SellerBadges';
import SellerReviewsList from './SellerReviewsList';
import '../marketplace-design.css';

const PERIODS: SellerPeriod[] = ['30d', '90d', '365d', 'all'];

const Kpi: React.FC<{ icon: string; label: string; value: string; sub?: string; testId: string; accent?: boolean }> = ({ icon, label, value, sub, testId, accent }) => (
    <div className="mk-kpi" data-testid={testId}>
        <div className="top">
            <span className="lbl">{label}</span>
            <IconBadge name={icon} size={36} />
        </div>
        <div className="v">{value}</div>
        {sub && <div className="d" style={accent ? { color: 'var(--color-secondary-dark)', fontWeight: 600 } : undefined}>{sub}</div>}
    </div>
);

const formatDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString('de-DE') : '–');
const monthLabel = (m: string): string => {
    const [y, mo] = m.split('-');
    return `${mo}/${y.slice(2)}`;
};

/**
 * Übersicht (Plan 3.7): KPI-Kacheln, Umsatzverlauf, letzte Verkäufe und der
 * Hinweis auf fehlende Rechnungsstammdaten bzw. PayPal-Verbindung. Kostenlose
 * Gruppen-Lizenzen zählen als Verkauf, nie im Umsatz.
 */
const SellerOverviewPanel: React.FC = () => {
    const translate = useTranslate();
    const [period, setPeriod] = useState<SellerPeriod>('365d');
    const [data, setData] = useState<SellerDashboard | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async (p: SellerPeriod) => {
        setError(null);
        try {
            setData(await marketplaceService.getSellerDashboard(p));
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceSeller.overview.loadError')));
        }
    }, [translate]);

    useEffect(() => {
        load(period);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    if (error) {
        return (
            <div className="mk-alert err" role="alert">
                <MaterialSymbol name="error" />
                <div className="body">{error}</div>
            </div>
        );
    }
    if (!data) return null;

    const { kpis, readiness } = data;
    const cur = data.currency;
    const chartData = data.monthly.map((m) => ({
        month: monthLabel(m.month),
        gross: parseFloat(m.gross),
        net: parseFloat(m.net),
    }));

    const saleAmount = (s: SellerSale): string => {
        if (s.origin === 'group') {
            return parseFloat(s.pricePaid) > 0 ? formatEuro(s.pricePaid, s.currency) : translate('marketplaceSeller.overview.groupFree');
        }
        return formatEuro(s.pricePaid, s.currency);
    };

    return (
        <div>
            {readiness.missing.length > 0 && (
                <div className="mk-alert warn" role="note">
                    <MaterialSymbol name="warning" />
                    <div className="body">
                        <b>{translate('marketplaceSeller.overview.readinessTitle')}</b>
                        <p>{readiness.missing.map((m) => translate(`marketplaceSeller.overview.missing.${m}`)).join(' ')}</p>
                    </div>
                    <Link to="/marketplace/seller/billing" className="mk-btn-o mk-sm act">
                        {translate('marketplaceSeller.overview.readinessAction')}
                    </Link>
                </div>
            )}

            <div className="mk-range-row">
                <div className="mk-seg-btns" role="group" aria-label={translate('marketplaceSeller.overview.period.label')}>
                    {PERIODS.map((p) => (
                        <button key={p} type="button" aria-pressed={p === period} onClick={() => setPeriod(p)}>
                            {translate(`marketplaceSeller.overview.period.${p}`)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mk-kpis">
                <Kpi testId="kpi-gross" icon="euro" label={translate('marketplaceSeller.kpi.gross')} value={formatEuro(kpis.grossRevenue, cur)} sub={translate('marketplaceSeller.kpi.grossHint')} />
                <Kpi
                    testId="kpi-sales"
                    icon="shopping_bag"
                    label={translate('marketplaceSeller.kpi.sales')}
                    value={String(kpis.salesCount)}
                    sub={translate('marketplaceSeller.kpi.salesGroup', { count: kpis.groupFreeCount })}
                />
                <Kpi testId="kpi-current" icon="schedule" label={translate('marketplaceSeller.kpi.currentNet')} value={formatEuro(kpis.currentPeriodNet, cur)} sub={translate('marketplaceSeller.kpi.currentNetHint')} accent />
                <Kpi testId="kpi-paid" icon="receipt_long" label={translate('marketplaceSeller.kpi.paidOut')} value={formatEuro(kpis.paidOutYear, cur)} sub={translate('marketplaceSeller.kpi.paidOutHint')} />
            </div>

            {parseFloat(kpis.internalChargeTotal) > 0 && (
                <div className="mk-alert info" role="note">
                    <MaterialSymbol name="info" />
                    <div className="body">
                        {translate('marketplaceSeller.overview.internalCharges', {
                            amount: formatEuro(kpis.internalChargeTotal, cur),
                            count: kpis.groupInternalCount,
                        })}
                    </div>
                </div>
            )}

            <div className="mk-chart">
                <h2>{translate('marketplaceSeller.overview.chartTitle')}</h2>
                <div style={{ width: '100%', height: 240 }}>
                    <ResponsiveContainer>
                        <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barGap={4}>
                            <CartesianGrid stroke={tokens.color.divider} strokeDasharray="3 4" vertical={false} />
                            <XAxis dataKey="month" stroke={tokens.color.neutral500} fontSize={12} tickLine={false} />
                            <YAxis stroke={tokens.color.neutral500} fontSize={12} width={48} tickLine={false} axisLine={false} />
                            <Tooltip formatter={(v) => formatEuro(Number(v), cur)} cursor={{ fill: tokens.color.neutral200 }} />
                            <Bar dataKey="gross" name={translate('marketplaceSeller.overview.series.gross')} fill={tokens.color.neutral900} radius={[3, 3, 0, 0]} maxBarSize={14} />
                            <Bar dataKey="net" name={translate('marketplaceSeller.overview.series.net')} fill={tokens.color.secondaryAccentDark} radius={[3, 3, 0, 0]} maxBarSize={14} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="mk-legend" aria-hidden="true">
                    <span><i style={{ background: tokens.color.neutral900 }} />{translate('marketplaceSeller.overview.series.gross')}</span>
                    <span><i style={{ background: tokens.color.secondaryAccentDark }} />{translate('marketplaceSeller.overview.series.net')}</span>
                </div>
            </div>

            <div className="mk-ovgrid">
                <div style={{ minWidth: 0 }}>
                    <div className="mk-sec-t">
                        <div><h2>{translate('marketplaceSeller.overview.recentSales')}</h2></div>
                        <Link to="/marketplace/seller/statements" className="mk-lnk">
                            {translate('marketplaceSeller.overview.allStatements')}
                            <MaterialSymbol name="arrow_forward" />
                        </Link>
                    </div>
                    {data.recentSales.length === 0 ? (
                        <div className="mk-empty" role="status">
                            <MaterialSymbol name="shopping_bag" />
                            <p>{translate('marketplaceSeller.overview.noSales')}</p>
                        </div>
                    ) : (
                        <>
                            <div className="mk-tbl-wrap wide">
                                <table className="mk-tbl">
                                    <thead>
                                        <tr>
                                            <th>{translate('marketplaceSeller.sales.date')}</th>
                                            <th>{translate('marketplaceSeller.sales.course')}</th>
                                            <th>{translate('marketplaceSeller.sales.buyer')}</th>
                                            <th>{translate('marketplaceSeller.sales.origin')}</th>
                                            <th>{translate('marketplaceSeller.sales.license')}</th>
                                            <th className="r">{translate('marketplaceSeller.sales.net')}</th>
                                            <th className="r">{translate('marketplaceSeller.sales.fee')}</th>
                                            <th className="r">{translate('marketplaceSeller.sales.share')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.recentSales.map((s) => (
                                            <tr key={s.id}>
                                                <td className="mono">{formatDate(s.createdAt)}</td>
                                                <td><b>{s.courseTitle}</b></td>
                                                <td>{s.buyerCompanyName ?? `#${s.buyerCompanyId}`}</td>
                                                <td><VisibilityBadge visibility={s.origin === 'group' ? 'group' : 'public'} short /></td>
                                                <td className="mut">
                                                    {translate(`marketplaceSeller.sales.types.${s.licenseType}`)}
                                                    {s.licenseType === 'seat_copy' && s.seats ? ` (${s.seats})` : ''}
                                                </td>
                                                <td className="r">{saleAmount(s)}</td>
                                                <td className="r mut">{s.origin === 'group' ? '–' : formatEuro(s.platformFee, s.currency)}</td>
                                                <td className="r"><b>{s.origin === 'group' ? '–' : formatEuro(s.licensorRevenue, s.currency)}</b></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="mk-cards">
                                {data.recentSales.map((s) => (
                                    <div key={s.id} className="mk-card">
                                        <div className="t">{s.courseTitle}</div>
                                        <div className="m">
                                            {formatDate(s.createdAt)} · {s.buyerCompanyName ?? `#${s.buyerCompanyId}`}
                                        </div>
                                        <div className="rowf">
                                            <VisibilityBadge visibility={s.origin === 'group' ? 'group' : 'public'} short />
                                            <span className="t">{saleAmount(s)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <SellerReviewsList />
            </div>
        </div>
    );
};

export default SellerOverviewPanel;
